import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';

const slides = [
  {
    id: 'despoluir',
    tag: 'PARCERIA',
    title: 'Rota da Eficiência Sustentável',
    subtitle: 'SSA DISTRIBUIDORA & Fetranscarga',
    description: 'O Programa Despoluir já é referência. Agora, com a SSA ao lado, cada diagnóstico ganha uma solução técnica imediata. Sua frota não para.',
    image: 'https://images.unsplash.com/photo-1766608422198-5be9ac0aac9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHx0cnVjayUyMGhpZ2h3YXklMjByb2FkJTIwZnJlaWdodCUyMHRyYW5zcG9ydGF0aW9ufGVufDB8fHx8MTc3NTUwNjg3OXww&ixlib=rb-4.1.0&q=85',
    link: '/noticias/rota-eficiencia-sustentavel',
    stats: [
      { value: '100%', label: 'Aprovação Despoluir' },
      { value: '15%', label: 'Redução Combustível' },
    ],
  },
];

export const NewsSlider = () => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % slides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length);
  }, [current, goTo]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section data-testid="news-slider" className="relative bg-gray-900 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={slide.image}
          alt=""
          className="w-full h-full object-cover opacity-20 transition-opacity duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/70" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Content */}
          <div className={`space-y-6 transition-all duration-600 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold tracking-wider rounded-full">
                {slide.tag}
              </span>
              <span className="text-gray-400 text-sm">Novidades SSA</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {slide.title}
            </h2>
            <p className="text-lg text-red-400 font-semibold">
              {slide.subtitle}
            </p>
            <p className="text-gray-300 text-base leading-relaxed max-w-lg">
              {slide.description}
            </p>

            {/* Stats */}
            {slide.stats && (
              <div className="flex gap-8 pt-2">
                {slide.stats.map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold text-red-500">{stat.value}</div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            <Link
              to={slide.link}
              data-testid="news-slider-cta"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 group"
            >
              Saiba Mais
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Image Card */}
          <div className={`hidden md:block transition-all duration-600 ${isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-700/50">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-80 object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent p-6">
                <div className="flex items-center gap-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_9d67a82a-11ea-4ca7-ab70-beec0433a409/artifacts/ooz54jpy_SSA_MONO_BRANCO.png"
                    alt="SSA"
                    className="h-10 w-auto"
                  />
                  <div className="h-8 w-px bg-gray-600" />
                  <span className="text-white text-sm font-semibold">Programa Despoluir</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        {slides.length > 1 && (
          <div className="flex items-center justify-between mt-10">
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current ? 'w-8 bg-red-500' : 'w-4 bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button
                onClick={prev}
                className="p-2 rounded-full border border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-500 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={next}
                className="p-2 rounded-full border border-gray-600 text-gray-400 hover:border-red-500 hover:text-red-500 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
